import React from 'react';
import { Target, Users, Award, CheckCircle, ArrowLeft } from 'lucide-react';
import { useData } from '../context/DataContext';

const AboutSection = ({ setActiveTab }) => {
  const { about, loading } = useData();

  if (loading || !about) {
    return (
      <section className="min-h-[calc(100vh-80px)] py-12 bg-white flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#1e3a5f] border-t-transparent rounded-full"></div>
      </section>
    );
  }

  return (
    <section className="min-h-[calc(100vh-80px)] py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => setActiveTab('home')}
          className="flex items-center gap-2 text-[#3d6a99] hover:text-[#1e3a5f] mb-8 transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm font-medium mb-4">
            About the Project
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Building Peaceful Schools Together
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {about.introduction}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Objectives */}
          <div className="bg-gradient-to-br from-[#f8fafc] to-white rounded-2xl p-8 border border-[#e2e8f0] shadow-sm hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a5f]">Objectives</h3>
            </div>
            <ul className="space-y-4">
              {(about.objectives || []).map((objective, index) => (
                <li key={index} className="flex items-start gap-3 group">
                  <CheckCircle className="w-5 h-5 text-[#3d6a99] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-gray-700 leading-relaxed">{objective}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Target Groups */}
          <div className="bg-gradient-to-br from-[#f8fafc] to-white rounded-2xl p-8 border border-[#e2e8f0] shadow-sm hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#2c5282] rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a5f]">Target Groups</h3>
            </div>
            <ul className="space-y-4">
              {(about.target_groups || []).map((group, index) => (
                <li key={index} className="flex items-start gap-3 group">
                  <CheckCircle className="w-5 h-5 text-[#3d6a99] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-gray-700 leading-relaxed">{group}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Expected Results */}
        <div className="bg-[#1e3a5f] rounded-3xl p-8 sm:p-12">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <Award className="w-8 h-8 text-[#90cdf4]" />
            <h3 className="text-2xl sm:text-3xl font-bold text-white">Expected Results</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(about.expected_results || []).map((result, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/20 transition-all duration-300 group"
              >
                <div className="w-8 h-8 bg-[#90cdf4] text-[#1e3a5f] rounded-lg flex items-center justify-center font-bold mb-3 group-hover:scale-110 transition-transform duration-300">
                  {index + 1}
                </div>
                <p className="text-white/90 leading-relaxed">{result}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
